import { useEffect, useState } from 'react'
import { Box, Card, CardBody, CardHeader, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { SpecOptionForm } from '@/components/forms/SpecOptionForm'
import { useThemeColors } from '@/hooks/useThemeColors'
import { getSpecOptionType } from '@/pages/AssetConfiguration/specOptionTypes'
import { AssetCatalogShell } from '@/components/assetCatalog/AssetCatalogShell'
import { useAssetCatalogPaths } from '@/hooks/useAssetCatalogPaths'

export default function SpecOptionEditPage() {
  const { specType, id } = useParams<{ specType: string; id: string }>()
  const config = getSpecOptionType(specType)
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { path } = useAssetCatalogPaths()
  const numericId = id ? Number(id) : undefined
  const [isValidating, setIsValidating] = useState(true)

  useEffect(() => {
    if (!numericId || Number.isNaN(numericId)) {
      navigate(path(specType ? `spec/${specType}` : 'overview'), { replace: true })
    } else {
      setIsValidating(false)
    }
  }, [numericId, navigate, path, specType])

  if (!config) {
    return <Navigate to={path('overview')} replace />
  }

  const handleDone = () => navigate(path(`spec/${config.slug}`))

  if (isValidating || !numericId) {
    return (
      <AssetCatalogShell fillHeight={false}>
        <Box p={4}>
          <Card>
            <CardBody display="flex" alignItems="center" justifyContent="center" minH="200px">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </AssetCatalogShell>
    )
  }

  return (
    <AssetCatalogShell fillHeight={false}>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Box minW={0} display="flex" flexDirection="column" overflow="hidden" flex="1">
          <Card h="full" display="flex" flexDirection="column">
            <CardHeader flexShrink={0}>
              <Heading size="md">Edit {config.label}</Heading>
              <Text fontSize="sm" color={colors.text} mt={1}>
                Update this {config.label.toLowerCase()} option
              </Text>
            </CardHeader>
            <CardBody overflowY="auto" flex="1" minH={0}>
              <SpecOptionForm
                config={config}
                entityId={numericId}
                onSuccess={handleDone}
                onCancel={handleDone}
              />
            </CardBody>
          </Card>
        </Box>
      </Box>
    </AssetCatalogShell>
  )
}
